import { createContext, useContext, useEffect, useState, useCallback, useRef, ReactNode } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "@/hooks/use-toast";
import { calculateCompatibility } from "@/lib/compatibilityEngine";
import type { Tables } from "@/integrations/supabase/types";
import { withTimeout } from "@/lib/supabaseAuthStability";

interface PropertyAlert {
  id: string;
  property: Tables<"properties">;
  score: number;
  label: "Alta" | "Média" | "Baixa";
  timestamp: string;
  read: boolean;
}

interface AvailabilityAlert {
  id: string;
  propertyId: string;
  propertyTitle: string;
  notificationType: string;
  sentAt: string;
}

export interface AppNotification {
  id: string;
  type: string;
  title: string;
  body: string | null;
  link: string | null;
  metadata: any;
  read_at: string | null;
  created_at: string;
}

interface NotificationContextType {
  unreadMessages: number;
  unreadMatches: number;
  unreadInterests: number;
  unreadRadar: number;
  unreadAvailability: number;
  pendingMatchActions: number;
  appNotifications: AppNotification[];
  unreadAppNotifications: number;
  radarAlerts: PropertyAlert[];
  availabilityAlerts: AvailabilityAlert[];
  clearMessageNotifications: () => void;
  clearMatchNotifications: () => void;
  clearRadarNotifications: () => void;
  clearAvailabilityNotifications: () => void;
  markAppNotificationRead: (id: string) => Promise<void>;
  markAllAppNotificationsRead: () => Promise<void>;
  requestBrowserPermission: () => void;
  browserPermission: NotificationPermission | "default";
}

const NotificationContext = createContext<NotificationContextType>({
  unreadMessages: 0,
  unreadMatches: 0,
  unreadInterests: 0,
  unreadRadar: 0,
  unreadAvailability: 0,
  pendingMatchActions: 0,
  appNotifications: [],
  unreadAppNotifications: 0,
  radarAlerts: [],
  availabilityAlerts: [],
  clearMessageNotifications: () => {},
  clearMatchNotifications: () => {},
  clearRadarNotifications: () => {},
  clearAvailabilityNotifications: () => {},
  markAppNotificationRead: async () => {},
  markAllAppNotificationsRead: async () => {},
  requestBrowserPermission: () => {},
  browserPermission: "default",
});

export const useNotifications = () => useContext(NotificationContext);

function extractMatchIdFromNotification(n: AppNotification): string | null {
  const md = (n.metadata ?? {}) as Record<string, unknown>;
  if (typeof md.match_interest_id === "string" && md.match_interest_id) return md.match_interest_id;
  const m = n.link?.match(/\/app\/(?:negotiation|messages)\/([^/?#]+)/);
  return m?.[1] ?? null;
}

async function fetchFinalizedMatchIds(matchIds: string[]): Promise<Set<string>> {
  if (matchIds.length === 0) return new Set();
  const pairIdsByMatchId = new Map<string, string[]>();
  const allRelatedIds = new Set(matchIds);

  await Promise.all(
    matchIds.map(async (matchId) => {
      const { data } = await supabase.rpc("get_match_pair_ids", { p_match_id: matchId });
      const pairIds = ((data as { id: string }[] | null) ?? []).map((row) => row.id);
      const ids = pairIds.length > 0 ? pairIds : [matchId];
      pairIdsByMatchId.set(matchId, ids);
      ids.forEach((id) => allRelatedIds.add(id));
    })
  );

  const { data } = await supabase
    .from("negotiations")
    .select("match_interest_id, status")
    .in("match_interest_id", Array.from(allRelatedIds))
    .in("status", ["cancelada", "concluida"]);
  const finalizedRelatedIds = new Set((data ?? []).map((n: any) => n.match_interest_id));
  return new Set(
    matchIds.filter((matchId) => {
      const ids = pairIdsByMatchId.get(matchId) ?? [matchId];
      return ids.some((id) => finalizedRelatedIds.has(id));
    })
  );
}

async function filterOutFinalizedReminders(list: AppNotification[]): Promise<AppNotification[]> {
  const reminderMatchIds = Array.from(
    new Set(
      list
        .filter((n) => n.type === "negotiation_reminder")
        .map(extractMatchIdFromNotification)
        .filter((v): v is string => Boolean(v))
    )
  );
  if (reminderMatchIds.length === 0) return list;
  const finalized = await fetchFinalizedMatchIds(reminderMatchIds);
  if (finalized.size === 0) return list;
  return list.filter((n) => {
    if (n.type !== "negotiation_reminder") return true;
    const id = extractMatchIdFromNotification(n);
    return !id || !finalized.has(id);
  });
}

async function isReminderForFinalizedNegotiation(n: AppNotification): Promise<boolean> {
  if (n.type !== "negotiation_reminder") return false;
  const id = extractMatchIdFromNotification(n);
  if (!id) return false;
  const finalized = await fetchFinalizedMatchIds([id]);
  return finalized.has(id);
}

export const NotificationProvider = ({ children }: { children: ReactNode }) => {
  const { user } = useAuth();
  const [unreadMessages, setUnreadMessages] = useState(0);
  const [unreadRadar, setUnreadRadar] = useState(0);
  const [unreadAvailability, setUnreadAvailability] = useState(0);
  const [radarAlerts, setRadarAlerts] = useState<PropertyAlert[]>([]);
  const [availabilityAlerts, setAvailabilityAlerts] = useState<AvailabilityAlert[]>([]);
  const [browserPermission, setBrowserPermission] = useState<NotificationPermission | "default">("default");
  const [myMatchInterestIds, setMyMatchInterestIds] = useState<Set<string>>(new Set());
  const [currentChatId, setCurrentChatId] = useState<string | null>(null);
  const [appNotifications, setAppNotifications] = useState<AppNotification[]>([]);
  const [pendingMatchActions, setPendingMatchActions] = useState(0);

  const myPropertiesRef = useRef<Tables<"properties">[]>([]);
  const myPrefsRef = useRef<Map<string, Tables<"swap_preferences">>>(new Map());

  // Track current route for suppressing notifications on active chat
  useEffect(() => {
    const checkRoute = () => {
      const path = window.location.pathname;
      const match = path.match(/\/app\/messages\/(.+)/);
      setCurrentChatId(match ? match[1] : null);
    };
    checkRoute();
    window.addEventListener("popstate", checkRoute);
    const observer = new MutationObserver(checkRoute);
    observer.observe(document.body, { childList: true, subtree: true });
    return () => {
      window.removeEventListener("popstate", checkRoute);
      observer.disconnect();
    };
  }, []);

  useEffect(() => {
    if ("Notification" in window) {
      setBrowserPermission(Notification.permission);
    }
  }, []);

  const requestBrowserPermission = useCallback(async () => {
    if ("Notification" in window && Notification.permission === "default") {
      const result = await Notification.requestPermission();
      setBrowserPermission(result);
    }
  }, []);

  const showBrowserNotification = useCallback((title: string, body: string, url?: string) => {
    if ("Notification" in window && Notification.permission === "granted" && document.hidden) {
      const notification = new Notification(title, {
        body,
        icon: "/favicon.ico",
        badge: "/favicon.ico",
      });
      if (url) {
        notification.onclick = () => {
          window.focus();
          window.location.href = url;
          notification.close();
        };
      }
    }
  }, []);

  // Load user's properties and prefs for radar
  useEffect(() => {
    if (!user) return;
    const loadUserData = async () => {
      try {
        const [propsRes, prefsRes] = await withTimeout(
          Promise.all([
            supabase.from("properties").select("*").eq("user_id", user.id).eq("is_active", true),
            supabase.from("swap_preferences").select("*").eq("user_id", user.id),
          ]),
          10000,
          "Tempo esgotado ao carregar notificações."
        );
        myPropertiesRef.current = propsRes.data ?? [];
        const prefsMap = new Map<string, Tables<"swap_preferences">>();
        (prefsRes.data ?? []).forEach(p => prefsMap.set(p.property_id, p));
        myPrefsRef.current = prefsMap;
      } catch (error) {
        console.warn("[Notifications] Falha ao carregar dados do usuário", error);
      }
    };
    loadUserData();
  }, [user]);

  // Load match interest IDs
  useEffect(() => {
    if (!user) return;
    const loadMatches = async () => {
      try {
        const { data: matches } = await withTimeout(
          supabase.rpc("get_matches", { p_user_id: user.id }),
          10000,
          "Tempo esgotado ao carregar matches."
        );
        if (matches) {
          setMyMatchInterestIds(new Set(matches.map((m: any) => m.match_interest_id)));
        }
      } catch (error) {
        console.warn("[Notifications] Falha ao carregar matches", error);
      }
    };
    loadMatches();
  }, [user]);

  // Count active matches needing user attention (not completed/cancelled)
  useEffect(() => {
    if (!user) {
      setPendingMatchActions(0);
      return;
    }
    const loadPending = async () => {
      try {
        const { data: matches } = await withTimeout(
          supabase.rpc("get_matches", { p_user_id: user.id }),
          10000,
          "Tempo esgotado ao carregar ações pendentes."
        );
        if (!matches || matches.length === 0) {
          setPendingMatchActions(0);
          return;
        }
        const ids = matches.map((m: any) => m.match_interest_id);
        const { data: negs } = await withTimeout(
          supabase
            .from("negotiations")
            .select("match_interest_id, status")
            .in("match_interest_id", ids),
          10000,
          "Tempo esgotado ao carregar negociações."
        );
        const finalizedIds = new Set(
          (negs ?? [])
            .filter((n: any) => n.status === "concluida" || n.status === "cancelada")
            .map((n: any) => n.match_interest_id)
        );
        const activeCount = ids.filter((id: string) => !finalizedIds.has(id)).length;
        setPendingMatchActions(activeCount);
      } catch (error) {
        console.warn("[Notifications] Falha ao carregar pendências", error);
        setPendingMatchActions(0);
      }
    };
    loadPending();
  }, [user, myMatchInterestIds]);

  // Load persistent app notifications + realtime
  useEffect(() => {
    if (!user) {
      setAppNotifications([]);
      return;
    }

    const load = async () => {
      try {
        const { data } = await withTimeout(
          supabase
            .from("app_notifications")
            .select("*")
            .eq("user_id", user.id)
            .order("created_at", { ascending: false })
            .limit(30),
          10000,
          "Tempo esgotado ao carregar notificações."
        );
        const all = (data ?? []) as AppNotification[];
        const filtered = await filterOutFinalizedReminders(all);
        setAppNotifications(filtered);
      } catch (error) {
        console.warn("[Notifications] Falha ao carregar notificações", error);
      }
    };
    load();

    const channel = supabase
      .channel(`app-notifications-${user.id}`)
      .on(
        "postgres_changes",
        { event: "INSERT", schema: "public", table: "app_notifications", filter: `user_id=eq.${user.id}` },
        async (payload) => {
          const notif = payload.new as AppNotification;
          if (await isReminderForFinalizedNegotiation(notif)) return;
          setAppNotifications(prev => [notif, ...prev].slice(0, 30));
          toast({
            title: notif.title,
            description: notif.body ?? undefined,
          });
          showBrowserNotification(notif.title, notif.body ?? "", notif.link ?? undefined);
        }
      )
      .on(
        "postgres_changes",
        { event: "UPDATE", schema: "public", table: "app_notifications", filter: `user_id=eq.${user.id}` },
        (payload) => {
          const updated = payload.new as AppNotification;
          setAppNotifications(prev => prev.map(n => (n.id === updated.id ? updated : n)));
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [user, showBrowserNotification]);

  // Listen for new messages via realtime
  useEffect(() => {
    if (!user) return;
    const matchIds = Array.from(myMatchInterestIds);
    if (matchIds.length === 0) return;

    const channel = supabase
      .channel(`notifications-messages-${user.id}`)
      .on(
        "postgres_changes",
        {
          event: "INSERT",
          schema: "public",
          table: "messages",
          filter: `match_interest_id=in.(${matchIds.join(",")})`,
        },
        (payload) => {
          const msg = payload.new as any;
          if (msg.sender_id !== user.id && myMatchInterestIds.has(msg.match_interest_id)) {
            if (currentChatId !== msg.match_interest_id) {
              setUnreadMessages((prev) => prev + 1);
              toast({
                title: "💬 Nova mensagem",
                description: msg.content.length > 50 ? msg.content.slice(0, 50) + "..." : msg.content,
              });
              showBrowserNotification(
                "Nova mensagem",
                msg.content.length > 80 ? msg.content.slice(0, 80) + "..." : msg.content,
                `/app/messages/${msg.match_interest_id}`
              );
            }
          }
        }
      )
      .subscribe();
    return () => { supabase.removeChannel(channel); };
  }, [user, myMatchInterestIds, currentChatId, showBrowserNotification]);

  // Track new mutual matches (still listen to interests for the match-interest-id set)
  useEffect(() => {
    if (!user) return;
    const channel = supabase
      .channel("notifications-interests")
      .on(
        "postgres_changes",
        { event: "INSERT", schema: "public", table: "interests" },
        async (payload) => {
          const interest = payload.new as any;
          if (interest.to_user_id === user.id) {
            const { data: myInterest } = await supabase
              .from("interests")
              .select("id")
              .eq("from_user_id", user.id)
              .eq("to_user_id", interest.from_user_id)
              .maybeSingle();
            if (myInterest) {
              setMyMatchInterestIds((prev) => new Set(prev).add(interest.id));
            }
          }
        }
      )
      .subscribe();
    return () => { supabase.removeChannel(channel); };
  }, [user]);

  // 🔔 RADAR
  useEffect(() => {
    if (!user) return;
    const channel = supabase
      .channel("radar-new-properties")
      .on(
        "postgres_changes",
        { event: "INSERT", schema: "public", table: "properties" },
        async (payload) => {
          const newProp = payload.new as Tables<"properties">;
          if (newProp.user_id === user.id) return;
          if (!newProp.is_active) return;
          const myProps = myPropertiesRef.current;
          if (myProps.length === 0) return;

          const { data: newPropPrefs } = await supabase
            .from("swap_preferences")
            .select("*")
            .eq("property_id", newProp.id)
            .maybeSingle();

          let bestScore = 0;
          let bestLabel: "Alta" | "Média" | "Baixa" = "Baixa";
          for (const myProp of myProps) {
            const myPref = myPrefsRef.current.get(myProp.id) || null;
            const result = calculateCompatibility(myProp, myPref, newProp, newPropPrefs);
            if (result.score > bestScore) {
              bestScore = result.score;
              bestLabel = result.label;
            }
          }

          if (bestScore >= 50) {
            const alert: PropertyAlert = {
              id: newProp.id,
              property: newProp,
              score: bestScore,
              label: bestLabel,
              timestamp: new Date().toISOString(),
              read: false,
            };
            setRadarAlerts(prev => [alert, ...prev].slice(0, 20));
            setUnreadRadar(prev => prev + 1);
            const propertyTypeLabels: Record<string, string> = {
              casa: "Casa", apartamento: "Apartamento", sobrado: "Sobrado",
              cobertura: "Cobertura", terreno: "Terreno", comercial: "Comercial",
              chacara: "Chácara",
            };
            const typeLabel = propertyTypeLabels[newProp.property_type] || newProp.property_type;
            const message = bestLabel === "Alta"
              ? `🎯 ${typeLabel} em ${newProp.city} — ${bestScore}% compatível!`
              : `📍 ${typeLabel} em ${newProp.city} — ${bestScore}% compatível`;
            toast({ title: "🔔 Novo imóvel compatível!", description: message });
            showBrowserNotification("Novo imóvel compatível!", message, "/app/engine");
          }
        }
      )
      .subscribe();
    return () => { supabase.removeChannel(channel); };
  }, [user, showBrowserNotification]);

  const clearMessageNotifications = useCallback(() => setUnreadMessages(0), []);
  const clearMatchNotifications = useCallback(async () => {
    if (!user) return;
    await supabase
      .from("app_notifications")
      .update({ read_at: new Date().toISOString() })
      .eq("user_id", user.id)
      .in("type", ["match", "interest_received"])
      .is("read_at", null);
  }, [user]);
  const clearRadarNotifications = useCallback(() => {
    setUnreadRadar(0);
    setRadarAlerts(prev => prev.map(a => ({ ...a, read: true })));
  }, []);
  const clearAvailabilityNotifications = useCallback(() => setUnreadAvailability(0), []);

  const markAppNotificationRead = useCallback(async (id: string) => {
    if (!user) return;
    await supabase
      .from("app_notifications")
      .update({ read_at: new Date().toISOString() })
      .eq("id", id)
      .eq("user_id", user.id);
  }, [user]);

  const markAllAppNotificationsRead = useCallback(async () => {
    if (!user) return;
    await supabase
      .from("app_notifications")
      .update({ read_at: new Date().toISOString() })
      .eq("user_id", user.id)
      .is("read_at", null);
  }, [user]);

  // Availability notifications (existing)
  useEffect(() => {
    if (!user) return;
    const loadAvailabilityAlerts = async () => {
      try {
        const { data } = await withTimeout(
          supabase
            .from("availability_notifications")
            .select("id, property_id, notification_type, sent_at")
            .eq("user_id", user.id)
            .is("responded_at", null)
            .order("sent_at", { ascending: false })
            .limit(20),
          10000,
          "Tempo esgotado ao carregar disponibilidade."
        );
        if (data && data.length > 0) {
          const propIds = [...new Set(data.map(d => d.property_id))];
          const { data: props } = await withTimeout(
            supabase
              .from("properties")
              .select("id, title")
              .in("id", propIds),
            10000,
            "Tempo esgotado ao carregar títulos dos imóveis."
          );
          const titleMap = new Map((props ?? []).map(p => [p.id, p.title]));
          const alerts: AvailabilityAlert[] = data.map(d => ({
            id: d.id,
            propertyId: d.property_id,
            propertyTitle: titleMap.get(d.property_id) || "Imóvel",
            notificationType: d.notification_type,
            sentAt: d.sent_at,
          }));
          setAvailabilityAlerts(alerts);
          setUnreadAvailability(alerts.length);
        }
      } catch (error) {
        console.warn("[Notifications] Falha ao carregar disponibilidade", error);
      }
    };
    loadAvailabilityAlerts();
  }, [user]);

  // Derived counters from persistent notifications
  const unreadAppNotifications = appNotifications.filter(n => !n.read_at).length;
  const unreadMatches = appNotifications.filter(n => !n.read_at && n.type === "match").length;
  const unreadInterests = appNotifications.filter(n => !n.read_at && n.type === "interest_received").length;

  return (
    <NotificationContext.Provider
      value={{
        unreadMessages,
        unreadMatches,
        unreadInterests,
        unreadRadar,
        unreadAvailability,
        pendingMatchActions,
        appNotifications,
        unreadAppNotifications,
        radarAlerts,
        availabilityAlerts,
        clearMessageNotifications,
        clearMatchNotifications,
        clearRadarNotifications,
        clearAvailabilityNotifications,
        markAppNotificationRead,
        markAllAppNotificationsRead,
        requestBrowserPermission,
        browserPermission,
      }}
    >
      {children}
    </NotificationContext.Provider>
  );
};
